import { EyeIcon } from 'lucide-react';
import { Reseller as NewReseller, Product } from 'randmar-api-client';
import { Button, ExternalProductCard, ProductCard, ResellerCard } from 'randmarcomps';
import React from 'react';
import { Link } from 'react-router-dom';
import Path from '../../routes/Path';
import Reseller from "../../stores/reseller/Reseller";
import SearchResult, { DocumentSearchResult, ManufacturerBid } from '../../stores/search/SearchResult';
import ProductActions from '../products/ProductActions';
import ResellerActions from '../reseller/ResellerActions';
import ButtonWithActions from '../util/ButtonWithActions';
import DocumentCard from './DocumentCard';
import OpportunityCard from './OpportunityCard';

interface ManufacturerGridItemProps {
  searchResult: SearchResult;
  applicationId: string;
}

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null;

const readString = (record: Record<string, unknown>, ...keys: string[]) => {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === 'string' && value.trim() !== '') return value.trim();
  }
  return null;
};

export const ManufacturerGridItem: React.FC<ManufacturerGridItemProps> = ({ searchResult, applicationId }) => {
  switch (searchResult.contentType) {
    case 'Document': {
      const document = searchResult.content as DocumentSearchResult;
      const rawDocument = isRecord(searchResult.newContent) ? searchResult.newContent : {};
      const documentNumber =
        document.documentNumber ||
        readString(rawDocument, 'DocumentNumber', 'documentNumber', 'ReceiptNumber', 'receiptNumber', 'RequisitionNumber', 'requisitionNumber', 'PurchaseOrderNumber', 'purchaseOrderNumber') ||
        'Invalid';
      return (
        <DocumentCard
          document={rawDocument}
          documentNumber={documentNumber}
          applicationId={applicationId}
        />
      );
    }

    case 'Reseller': {
      const oldReseller = searchResult.content as Reseller;
      const newReseller = searchResult.newContent as NewReseller;
      return (
        <ResellerCard
          reseller={newReseller}
          link={Path.reseller(applicationId, newReseller.ResellerId ?? '')}
          linkNewTab={false}
          actions={
            <div className="flex">
              <ButtonWithActions buttonText="Actions" variant="secondary">
                <ResellerActions reseller={oldReseller} />
              </ButtonWithActions>
            </div>
          }
          mainAction={
            <Button asChild className="w-full sm:w-auto">
              <Link to={Path.reseller(applicationId, newReseller.ResellerId ?? '')}>
                <EyeIcon className="mr-2 h-4 w-4" /> View Reseller
              </Link>
            </Button>
          }
        />
      );
    }

    case 'Opportunity':
      return <OpportunityCard opportunities={searchResult.content as ManufacturerBid} />;

    case 'Product': {
      const product = searchResult.newContent as Product;
      if (applicationId !== product.ManufacturerId) {
        return (
          <ExternalProductCard
            product={product}
            viewProductLink={Path.product(applicationId, product.RandmarSKU ?? '')}
          />
        );
      }
      return (
        <ProductCard
          applicationId={applicationId}
          product={product}
          viewProductLink={Path.product(applicationId, product.RandmarSKU ?? '')}
          customAction={
            applicationId === product.ManufacturerId && (
              <ProductActions product={product} />
            )
          }
        />
      );
    }

    default:
      return null;
  }
};
